import { FC, MouseEvent } from 'react'
import styled from 'styled-components'
import { useConfirmationModalContext } from 'Hooks/modals/useConfirmationModal'
import { useDeleteFeeScheduleQuery } from 'Hooks/api/JOONAdmin/useDeleteFeeScheduleQuery'
import { useEmployerInfoContext } from 'Contexts/EmployerInfoContext'
import { FlexColumn, FlexRow, spacingMD, spacingSM, spacingXXS } from 'Utils/styles/spacing'
import { fontSizeXS, fontSizeSM, fontWeightBold, fontWeightSlightlyBold } from 'Utils/styles/text'
import { black, mineralGreen, elfGreen } from 'Utils/styles/colors'
import { EmployerCountry, FeeSchedule } from 'Utils/types/employerInfo'
import { CountryCode, CountryNames } from 'Utils/types/countries'
import { displayDate } from 'Utils/helpers/dateUtils'

const ScheduleSection = styled(FlexColumn)`
  padding: 0 ${spacingMD} ${spacingSM};
  gap: ${spacingSM};
`

const ScheduleGroup = styled(FlexColumn)`
  gap: ${spacingXXS};
`

const HeadingRow = styled(FlexRow)`
  justify-content: space-between;
  align-items: baseline;
  gap: ${spacingMD};
`

const Heading = styled.span`
  font-size: ${fontSizeSM};
  font-weight: ${fontWeightBold};
  color: ${black};
`

const Actions = styled(FlexRow)`
  gap: ${spacingMD};
`

const DetailRow = styled(FlexRow)`
  gap: ${spacingXXS};
`

const Label = styled.span`
  font-size: ${fontSizeXS};
  color: ${mineralGreen};
  min-width: fit-content;
`

const Value = styled.span`
  font-size: ${fontSizeXS};
  font-weight: ${fontWeightSlightlyBold};
  color: ${black};
`

const LinkButton = styled.button`
  background: none;
  border: none;
  color: ${elfGreen};
  cursor: pointer;
  font-size: ${fontSizeXS};
  padding: 0;
  text-align: left;
  text-decoration: underline;
  width: fit-content;
`

const formatMonth = (dateStr: string) => displayDate(new Date(`${dateStr}T00:00:00`), false)

const ScheduleDetails: FC<{ schedule: FeeSchedule }> = ({ schedule }) => (
  <>
    <DetailRow>
      <Label>Processing fee per:</Label>
      <Value>${schedule.processingFee}</Value>
    </DetailRow>
    <DetailRow>
      <Label>Charge per type:</Label>
      <Value>{schedule.chargeFeePer}</Value>
    </DetailRow>
    <DetailRow>
      <Label>Alternative reimbursement:</Label>
      <Value>{schedule.alternativeReimbursement ? 'enabled' : 'disabled'}</Value>
    </DetailRow>
  </>
)

type CountryFeeScheduleProps = {
  countryConfig: EmployerCountry
  onOpenFeeScheduleModal: (countryCode: CountryCode, feeSchedule: FeeSchedule | null) => void
}

export const CountryFeeSchedule: FC<CountryFeeScheduleProps> = ({
  countryConfig,
  onOpenFeeScheduleModal
}) => {
  const { countryCode, feeSchedule, pricingDescription } = countryConfig
  const currentSchedule = feeSchedule?.current
  const upcomingSchedule = feeSchedule?.next

  const {
    state: { employerInfo }
  } = useEmployerInfoContext()
  const { showModal } = useConfirmationModalContext()
  const { mutate: deleteFeeSchedule } = useDeleteFeeScheduleQuery()

  const openAdd = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    onOpenFeeScheduleModal(countryCode, null)
  }

  const openEditCurrent = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    if (currentSchedule) onOpenFeeScheduleModal(countryCode, currentSchedule)
  }

  const openEditUpcoming = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    if (upcomingSchedule) onOpenFeeScheduleModal(countryCode, upcomingSchedule)
  }

  const handleDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    if (!employerInfo?.id || !upcomingSchedule) return

    const confirmed = await showModal(
      `Remove fee schedule: ${CountryNames[countryCode]}`,
      `Remove the upcoming fee schedule effective ${formatMonth(upcomingSchedule.startsOn)}?`
    )
    if (confirmed) {
      deleteFeeSchedule({ employerId: employerInfo.id, feeScheduleId: upcomingSchedule.id })
    }
  }

  return (
    <ScheduleSection>
      {pricingDescription && (
        <DetailRow>
          <Label>Pricing description:</Label>
          <Value>{pricingDescription}</Value>
        </DetailRow>
      )}
      {currentSchedule && (
        <ScheduleGroup>
          <HeadingRow>
            <Heading>Current fee schedule</Heading>
            {currentSchedule.editable && (
              <Actions>
                <LinkButton onClick={openEditCurrent}>Edit</LinkButton>
              </Actions>
            )}
          </HeadingRow>
          <Label>since {formatMonth(currentSchedule.startsOn)}</Label>
          <ScheduleDetails schedule={currentSchedule} />
        </ScheduleGroup>
      )}

      {upcomingSchedule ? (
        <ScheduleGroup>
          <HeadingRow>
            <Heading>Upcoming fee schedule</Heading>
            <Actions>
              {upcomingSchedule.editable && (
                <LinkButton onClick={openEditUpcoming}>Edit</LinkButton>
              )}
              <LinkButton onClick={handleDelete}>Delete</LinkButton>
            </Actions>
          </HeadingRow>
          <Label>effective {formatMonth(upcomingSchedule.startsOn)}</Label>
          <ScheduleDetails schedule={upcomingSchedule} />
        </ScheduleGroup>
      ) : (
        <LinkButton onClick={openAdd}>Add upcoming fee schedule</LinkButton>
      )}
    </ScheduleSection>
  )
}
